// src/components/Navbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const servicesLinks = [
    { label: "Managed Services", href: "https://people-click.com/managed-services.html" },
    { label: "Enterprise Solutions", href: "https://people-click.com/enteprise-solutions.html" },
    { label: "Application Maintenance & Support", href: "https://people-click.com/application-maintenance-support.html" },
    { label: "Infrastructure Management Services", href: "https://people-click.com/infrastructure-management-services.html" },
    { label: "Testing Services", href: "https://people-click.com/testing-services.html" },
    { label: "Captive Insourcing", href: "https://people-click.com/captive-insourcing.html" },
    { label: "Digital Marketing", href: "https://people-click.com/digital-marketing.html" },
];

const industriesLinks = [
    { label: "Automotive", href: "https://people-click.com/automotive.html" },
    { label: "Consumer Products", href: "https://people-click.com/consumer-products.html" },
    { label: "Health Care and Life Sciences", href: "https://people-click.com/healthcare-life-sciences.html" },
    { label: "Retail", href: "https://people-click.com/retail.html" },
    { label: "Public Sector", href: "https://people-click.com/public-sector.html" },
    { label: "Manufacturing", href: "https://people-click.com/manufacturing.html" },
    { label: "Energy", href: "https://people-click.com/energy.html" },
];

const aboutLinks = [
    { label: "Our Profile", href: "https://people-click.com/peopleclick-profile.html" },
    { label: "Leadership Team", href: "https://people-click.com/leadership-team.html" },
];

const productsLinks = [
    { label: "Audit Platform", href: "/audit" },
    { label: "Cyber Security", href: "/cyber-security" },
    { label: "AI Landing Page", href: "/ai-landing" },
];

const mainLinks = [
    { label: "Home", href: "https://people-click.com/" },
    { label: "Strategic Consulting", href: "https://people-click.com/strategic-consulting.html" },
    { label: "Technologies", href: "https://people-click.com/technologies.html" },
    { label: "Products", dropdown: productsLinks },
    { label: "Services", dropdown: servicesLinks },
    { label: "Industries", dropdown: industriesLinks },
    { label: "About us", dropdown: aboutLinks },
    { label: "Contact", href: "#contact" },
];

const ChevronDown = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const DropdownMenu = ({ items, isOpen, currentView }) => (
    <div
        className={`pc-dropdown-menu ${isOpen ? "pc-dropdown-open" : ""}`}
        role="menu"
    >
        {items.map((item) => {
            const isInternal = !item.href.startsWith("http");
            const isActive = isInternal && item.href.replace("/", "") === currentView;
            if (isInternal) {
                return (
                    <Link
                        key={item.label}
                        to={item.href}
                        className={`pc-dropdown-item ${isActive ? "!bg-violet/10 !text-violet font-semibold border-l-2 border-violet" : ""}`}
                        role="menuitem"
                    >
                        {item.label}
                    </Link>
                );
            }
            return (
                <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pc-dropdown-item"
                    role="menuitem"
                >
                    {item.label}
                </a>
            );
        })}
    </div>
);

const Navbar = ({ currentView }) => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [mobileExpanded, setMobileExpanded] = useState(null);
    const navRef = useRef(null);

    const isProductActive = ["audit", "citadel-identity", "ai-camera", "aieco-suite", "ai-solutions", "cyber-security", "ai-landing"].includes(currentView);

    // Scroll handler — toggle glass effect
    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) {
                setOpenDropdown(null);
                setMobileOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const toggleDropdown = (label) =>
        setOpenDropdown((prev) => (prev === label ? null : label));

    const toggleMobileExpand = (label) =>
        setMobileExpanded((prev) => (prev === label ? null : label));

    return (
        <>
            {/* ─── Top bar ─── */}
            <div className="pc-topbar">
                <div className="pc-topbar-inner">
                    <div className="pc-topbar-countries">
                        {["INDIA", "USA", "CANADA", "FRANCE", "UK", "UAE"].map((c) => (
                            <span key={c} className="pc-topbar-country">{c}</span>
                        ))}
                    </div>
                    <a href="mailto:info@people-click.com" className="pc-topbar-email !hidden md:!flex">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                            <polyline points="22,6 12,13 2,6" />
                        </svg>
                        info@people-click.com
                    </a>
                </div>
            </div>

            {/* ─── Main Navbar ─── */}
            <nav
                ref={navRef}
                className={`pc-navbar ${scrolled ? "pc-navbar-glass" : "pc-navbar-solid"}`}
                id="main-navbar"
            >
                <div className="pc-navbar-inner">
                    {/* Logo */}
                    <a href="https://people-click.com/" target="_self" rel="noopener noreferrer" className="pc-logo-link">
                        <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="Peopleclick" className="pc-logo-img" />
                    </a>

                    {/* Desktop Nav Links */}
                    <ul className="pc-nav-links">
                        {mainLinks.map((link) => {
                            const isParentActive = (link.label === "Products" && isProductActive);
                            return link.dropdown ? (
                                <li key={link.label} className="pc-nav-item pc-has-dropdown" onMouseLeave={() => setOpenDropdown(null)}>
                                    <button
                                        className={`pc-nav-btn ${isParentActive ? "pc-nav-active" : ""}`}
                                        aria-haspopup="true"
                                        aria-expanded={openDropdown === link.label}
                                        onClick={() => toggleDropdown(link.label)}
                                        onMouseEnter={() => setOpenDropdown(link.label)}
                                    >
                                        {link.label}
                                        <span className={`pc-chevron ${openDropdown === link.label ? "pc-chevron-up" : ""}`}>
                                            <ChevronDown />
                                        </span>
                                    </button>
                                    <DropdownMenu items={link.dropdown} isOpen={openDropdown === link.label} currentView={currentView} />
                                </li>
                            ) : (
                                <li key={link.label} className="pc-nav-item">
                                    <a
                                        href={link.href}
                                        className="pc-nav-link"
                                        target={link.href.startsWith("http") ? "_blank" : "_self"}
                                        rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Mobile hamburger */}
                    <button
                        className="pc-hamburger"
                        aria-label="Toggle menu"
                        aria-expanded={mobileOpen}
                        onClick={() => setMobileOpen((o) => !o)}
                    >
                        <span className={`pc-ham-bar ${mobileOpen ? "pc-ham-bar1-open" : ""}`} />
                        <span className={`pc-ham-bar ${mobileOpen ? "pc-ham-bar2-open" : ""}`} />
                        <span className={`pc-ham-bar ${mobileOpen ? "pc-ham-bar3-open" : ""}`} />
                    </button>
                </div>

                {/* ─── Mobile Menu ─── */}
                <div className={`pc-mobile-menu ${mobileOpen ? "pc-mobile-open" : ""}`}>
                    <ul className="pc-mobile-links">
                        {mainLinks.map((link) => {
                            const isParentActive = (link.label === "Products" && isProductActive);
                            return link.dropdown ? (
                                <li key={link.label} className="pc-mobile-item">
                                    <button
                                        className={`pc-mobile-btn ${isParentActive ? "pc-mobile-active text-violet" : ""}`}
                                        onClick={() => toggleMobileExpand(link.label)}
                                    >
                                        {link.label}
                                        <span className={`pc-chevron ${mobileExpanded === link.label ? "pc-chevron-up" : ""}`}>
                                            <ChevronDown />
                                        </span>
                                    </button>
                                    <div className={`pc-mobile-sub ${mobileExpanded === link.label ? "pc-mobile-sub-open" : ""}`}>
                                        {link.dropdown.map((sub) => {
                                            const isSubInternal = !sub.href.startsWith("http");
                                            const isSubActive = isSubInternal && sub.href.replace("/", "") === currentView;
                                            if (isSubInternal) {
                                                return (
                                                    <Link
                                                        key={sub.label}
                                                        to={sub.href}
                                                        className={`pc-mobile-sub-link ${isSubActive ? "pc-mobile-active font-semibold !text-violet bg-violet/10" : ""}`}
                                                        onClick={() => setMobileOpen(false)}
                                                    >
                                                        {sub.label}
                                                    </Link>
                                                );
                                            }
                                            return (
                                                <a
                                                    key={sub.label}
                                                    href={sub.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="pc-mobile-sub-link"
                                                    onClick={() => setMobileOpen(false)}
                                                >
                                                    {sub.label}
                                                </a>
                                            );
                                        })}
                                    </div>
                                </li>
                            ) : (
                                <li key={link.label} className="pc-mobile-item">
                                    <a
                                        href={link.href}
                                        className="pc-mobile-link"
                                        target={link.href.startsWith("http") ? "_blank" : "_self"}
                                        rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        {link.label}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </nav>

            {/* Hover-close overlay for dropdowns */}
            {openDropdown && (
                <div
                    className="pc-dropdown-overlay"
                    onMouseEnter={() => setOpenDropdown(null)}
                />
            )}
        </>
    );
};

export default Navbar;
